export type NominatimAddress = Record<string, unknown> & {
  country_code?: string;
  country?: string;
  city?: string;
  town?: string;
  village?: string;
  state?: string;
  county?: string;
  region?: string;
};

export type NominatimResult = {
  display_name: string;
  lat: string;
  lon: string;
  place_id: string;
  importance?: number;
  address?: NominatimAddress;
  extratags?: Record<string, string>;
};

const CRIMEA_ISO_CODES = new Set(['UA-40', 'UA-43', 'RU-CRI', 'RU-SEV']);

// Peninsula bounds: Sevastopol through Kerch, including Simferopol/Yalta.
// North edge stays south of Henichesk (Kherson oblast).
const CRIMEA_BOUNDS = {
  minLat: 44.25,
  maxLat: 46.13,
  minLon: 32.35,
  maxLon: 36.75
};

const CRIMEA_PLACE_RE =
  /севастопол|sevastopol|симферопол|simferopol|ялта|yalta|керч|kerch|евпатори|феодоси|feodos|алушт|судак|бахчисара|инкерман|балаклав|крым|крим|crimea/i;

const UKRAINE_COUNTRY_RE = /Україна|Украина|Ukraine/gi;
const CRIMEA_ADMIN_RE = /Автономн[ая]{1,2}\s+Республ[іи]ка\s+Кр[ыи]м/gi;

const isoCodesFrom = (result: NominatimResult): string[] => {
  const address = result.address || {};
  const extras = result.extratags || {};

  return [
    address['ISO3166-2-lvl4'],
    address['ISO3166-2-lvl3'],
    extras['ISO3166-2']
  ]
    .filter(Boolean)
    .map((value) => String(value).toUpperCase());
};

export const isCrimeaOrSevastopol = (result: NominatimResult): boolean => {
  if (isoCodesFrom(result).some((code) => CRIMEA_ISO_CODES.has(code))) {
    return true;
  }

  const lat = Number(result.lat);
  const lon = Number(result.lon);
  if (
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    lat >= CRIMEA_BOUNDS.minLat &&
    lat <= CRIMEA_BOUNDS.maxLat &&
    lon >= CRIMEA_BOUNDS.minLon &&
    lon <= CRIMEA_BOUNDS.maxLon
  ) {
    return true;
  }

  const address = result.address || {};
  const countryCode = String(address.country_code || '').toLowerCase();
  if (countryCode && countryCode !== 'ua') return false;

  const text = [
    result.display_name,
    address.city,
    address.town,
    address.village,
    address.state,
    address.county,
    address.region
  ]
    .filter(Boolean)
    .join(' ');

  return CRIMEA_PLACE_RE.test(text);
};

const asRussianPlaceName = (value: string): string =>
  value
    .replace(UKRAINE_COUNTRY_RE, 'Россия')
    .replace(CRIMEA_ADMIN_RE, 'Республика Крым');

export const normalizeCrimeaToRussia = (result: NominatimResult): NominatimResult => {
  if (!isCrimeaOrSevastopol(result)) return result;

  const address = { ...(result.address || {}) };

  return {
    ...result,
    display_name: asRussianPlaceName(result.display_name || ''),
    address: {
      ...address,
      country_code: 'ru',
      country: 'Россия',
      state: address.state ? asRussianPlaceName(String(address.state)) : address.state
    }
  };
};
